import React, { useState, useEffect } from 'react';
import axiosInstance from '../AxiosInstance';
import { 
  Container, 
  TextField, 
  Button, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Snackbar, 
  Card, 
  CardContent, 
  Typography, 
  Box 
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useLocation, useNavigate } from 'react-router-dom';

const AddInventory = () => {
  const [currentStock, setCurrentStock] = useState('');
  const [stockLimit, setStockLimit] = useState(''); // Low stock threshold
  const [targetStock, setTargetStock] = useState(''); // Optional target stock after reordering
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitId, setUnitId] = useState(''); 
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [productInfo, setProductInfo] = useState({ id: '', name: '', brand: '', variety: '', size: '' });
  const [fromOverview, setFromOverview] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Auto-calculate threshold when current stock changes
  useEffect(() => {
    if (currentStock && !isNaN(currentStock) && parseFloat(currentStock) > 0) {
      const calculatedThreshold = Math.min(parseFloat(currentStock) * 0.1, 1);
      setStockLimit(calculatedThreshold.toString());
    }
  }, [currentStock]);

  useEffect(() => {
    // Get product information from URL parameters
    const queryParams = new URLSearchParams(location.search);
    const productId = queryParams.get('product_id');
    const productNameParam = queryParams.get('product_name');
    const varietyParam = queryParams.get('variety');
    const fromOverviewParam = queryParams.get('from_overview');

    setFromOverview(fromOverviewParam === 'true');
    
    if (!productId) {
      // If no product ID in URL, redirect back to add product
      navigate('/dashboard/products/add');
      return;
    }

    const initialName = productNameParam ? decodeURIComponent(productNameParam) : '';
    const initialVariety = varietyParam ? decodeURIComponent(varietyParam) : '';

    const fetchProductDetails = async () => {
      try {
        const response = await axiosInstance.get(`/products/${productId}`);
        const product = response.data || {};
        setProductInfo({
          id: productId,
          name: product.product_name || initialName,
          brand: product.brand || '',
          variety: product.variety || initialVariety,
          size: product.size || ''
        });
      } catch (error) {
        console.error('Error fetching product details:', error);
        setProductInfo({
          id: productId,
          name: initialName || 'Product',
          brand: '',
          variety: initialVariety,
          size: ''
        });
      }
    };

    fetchProductDetails();

    // Fetch units for this specific product (using unitInfo endpoint)
    const fetchUnits = async () => {
      try {
        setUnitsLoading(true);
        const unitsResponse = await axiosInstance.get(`/units/product/${productId}/unitInfo`);
        const raw = unitsResponse.data;
        const fetchedUnits = Array.isArray(raw)
          ? raw
          : raw && Array.isArray(raw.units)
          ? raw.units
          : [];

        setUnits(fetchedUnits);

        // Auto-select a default unit if one exists and none is currently selected
        if (fetchedUnits.length > 0) {
          setUnitId((prev) => prev || String(fetchedUnits[0].unit_id));
        }
      } catch (error) {
        console.error('Error fetching units:', error);
        setSnackbarMessage('Error fetching units for this product');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setUnitsLoading(false);
      }
    };

    fetchUnits();
  }, [location, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // If no units exist for this product, block submission and guide the user
    if (!units.length) {
      setSnackbarMessage('Please add at least one unit for this product before entering current stock.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    if (!unitId || !currentStock || !stockLimit) {
      setSnackbarMessage('Please fill in all required fields');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Validate numeric fields
    if (isNaN(parseFloat(currentStock)) || parseFloat(currentStock) <= 0) {
      setSnackbarMessage('Current stock must be a valid positive number');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (isNaN(parseFloat(stockLimit)) || parseFloat(stockLimit) < 0) {
      setSnackbarMessage('Stock limit must be a valid number');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (isNaN(parseInt(unitId))) {
      setSnackbarMessage('Please select a valid unit');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const inventoryData = {
        product_id: productInfo.id, // Use product_id directly instead of name/variety lookup
        current_stock: parseFloat(currentStock),
        stock_limit: parseFloat(stockLimit),
        unit_id: parseInt(unitId)
      };

      // Optional target_stock: include only if provided and valid number
      if (targetStock !== '' && !isNaN(parseFloat(targetStock)) && parseFloat(targetStock) >= 0) {
        inventoryData.target_stock = parseFloat(targetStock);
      }
      
      console.log('Sending inventory data:', inventoryData);
      
      await axiosInstance.post('/inventories', inventoryData);
      
      setSnackbarMessage('Current stock entered successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // After successful submission, navigate back to Add Product after a delay
      setTimeout(() => {
        navigate('/dashboard/products/add');
      }, 2000);
      
    } catch (error) {
      console.error('Error adding inventory:', error);
      
      // Get specific error message from backend
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error entering current stock';
      
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getProductDisplayName = () => {
    if (productInfo.variety) {
      return `${productInfo.name} - ${productInfo.variety}`;
    }
    return productInfo.name;
  };

  const getLongProductDisplayName = () => {
    const parts = [];
    if (productInfo.name) parts.push(productInfo.name);
    if (productInfo.brand) parts.push(productInfo.brand);
    if (productInfo.variety) parts.push(productInfo.variety);
    if (productInfo.size) parts.push(productInfo.size);
    return parts.join(' - ');
  };

  return (
    <Container sx={{ marginTop: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            Enter Current Stock of {getLongProductDisplayName() || getProductDisplayName()}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Product info display - read only */}
              <TextField
                label="Product"
                value={getProductDisplayName()}
                fullWidth
                disabled
                variant="filled"
              />

              {!unitsLoading && units.length === 0 && (
                <>
                  <MuiAlert severity="info" sx={{ mt: 1 }}>
                    No units have been configured for this product yet. Please add at least one unit before entering current stock.
                  </MuiAlert>
                  <Button
                    onClick={() =>
                      navigate(
                        `/dashboard/units/add?product_id=${encodeURIComponent(
                          productInfo.id
                        )}&from_product=true`
                      )
                    }
                    variant="outlined"
                    color="primary"
                  >
                    Add Units for this Product
                  </Button>
                </>
              )}

              <FormControl fullWidth required disabled={units.length === 0}>
                <InputLabel>Unit</InputLabel>
                <Select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                  {units.map((unit) => (
                    <MenuItem key={unit.unit_id} value={unit.unit_id}>
                      {unit.unit_type} ({unit.unit_category}) 
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Current Stock"
                type="number"
                value={currentStock}
                onChange={(e) => setCurrentStock(e.target.value)}
                fullWidth
                required
                disabled={units.length === 0}
              />

              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.75rem',
                  marginBottom: 1,
                  marginTop: 2 
                }}
              >
                You will be warned that the stock of "{getProductDisplayName()}" is low if it falls below:
              </Typography>

              <TextField
                label="Low Stock Threshold *"
                type="number"
                value={stockLimit}
                onChange={(e) => setStockLimit(e.target.value)}
                fullWidth
                required
                disabled={units.length === 0}
              />

              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.75rem',
                  marginBottom: 1,
                  marginTop: 2 
                }}
              >
                What target stock do you want for "{getProductDisplayName()}" after reordering?
              </Typography>

              <TextField
                label="Target Stock for Reordering (optional)"
                type="number"
                value={targetStock}
                onChange={(e) => setTargetStock(e.target.value)}
                fullWidth
                inputProps={{ min: 0 }}
                helperText="Desired stock level after restocking"
                disabled={units.length === 0}
              />

              <Button type="submit" variant="contained" color="primary" disabled={units.length === 0}>
                DONE
              </Button>
              
              {!fromOverview && (
                <Button 
                  onClick={() => navigate('/dashboard/products/add')} 
                  variant="outlined" 
                  color="secondary"
                >
                  Back to Add Product
                </Button>
              )}
            </Box>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default AddInventory;
